import React, { ReactElement, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Typography } from '@material-ui/core';
import { styled } from '@material-ui/styles';
import Container from '../../blocks/Container';
import { Aside, Content } from '../../blocks/FlexLayout';
import TopNav from '../../blocks/TopNav';
import { BlockedTabIcon, DismissedTabIcon } from '../../../atoms/icons/BlockedDismissedIcons';
import breakpoints from '../../../theme/material/breakpoints';
import Blocked from './Blocked';
import {
  deleteDismissProfile,
  getBlockedProfileList,
  getDismissedProfileList,
  toggleBlockProfile,
} from '../../../actions/profileActions';
import colors from '../../../theme/colors';
import { isEmpty } from 'lodash';
import CustomNotificationAlert from '../../blocks/CustomNotificationAlert';
import { isDarkMode } from '../../../utils/helpers';
import { getBorders } from '../../../style/index';
import useBreakpointQuery from '../../../hooks/useBreakPointQuery';
import { useDisableBodyScroll } from '../../../hooks/useDisableBodyScroll';
import LoadingBackdrop from '../../../atoms/loader/LoadingBackdrop';
import { RootState } from '../../../store';

const ListContainer = styled('div')({
  width: '60%',
  [breakpoints.down('sm')]: {
    width: '100%',
  },
});

const ListWrapper = styled('div')(() => ({
  background: isDarkMode() ? 'rgb(4, 26, 42)' : 'white',
  padding: '20px 10px',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '10px',
  justifyContent: 'center',
  gap: 10,
  [breakpoints.up(769)]: {
    border: isDarkMode() ? `${getBorders().DEFAULT}` : 'none',
    boxShadow: isDarkMode() ? `0 1px 4px 0 rgba(0, 0, 0, 0.04)` : 'none',
    borderRadius: isDarkMode() ? '6px' : '10px',
  },
}));

const HeaderTitle = styled(Typography)(() => ({
  fontSize: 18,
  fontWeight: 400,
  lineHeight: '21px',
  letterSpacing: '-0.36px',
  color: isDarkMode() ? 'rgb(140, 140, 140)' : colors.light.blue.secondary,
}));

const Caption = styled(Typography)(() => ({
  fontSize: 13,
  fontWeight: 400,
  lineHeight: '18px',
  letterSpacing: '-0.26px',
  color: isDarkMode() ? '#8C8C8C' : '#8F8F8F',
  fontStyle: 'italic',
  padding: '10px 0px 20px 0px',
  borderBottom: getBorders().DEFAULT,
  marginBottom: '-10px',
}));

const BlockedDismissed = (): ReactElement => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    blockedItems,
    dismissedProfiles,
    pagination,
    dismissedPagination,
    isFetchingProfileList,
    isDeletedLoader,
  } = useSelector((state: RootState) => state.blocked);

  useDisableBodyScroll(isDeletedLoader);

  const [subRoutes] = useState([
    {
      id: 'dismissed',
      page: 'Dismissed',
      title: 'Dismissed Profiles',
      icon: {
        default: <DismissedTabIcon isActive={false} />,
        active: <DismissedTabIcon isActive={true} />,
      },
      url: '/dismissed',
      active: /.*dismissed$/,
      caption:
        'These profiles have been temporarily hidden from you because you have either dismissed them or declined their offer.',
    },
    {
      id: 'blocked',
      page: 'Blocked',
      title: 'Blocked Profiles',
      icon: {
        default: <BlockedTabIcon isActive={false} />,
        active: <BlockedTabIcon isActive={true} />,
      },
      url: '/blocked',
      active: /.*blocked.*/,
      caption:
        'These profiles have been blocked by you. All current and future messages and offers will not be shown to you.',
    },
  ]);

  const [moreExists, setMoreExists] = useState(false);
  const isBreakpointDown600 = useBreakpointQuery({ key: 'down', value: 600 });

  const messageAction = (successMsg = '') => {
    const success = <CustomNotificationAlert icon={<></>} title={successMsg} />;
    const error = <CustomNotificationAlert icon={<></>} title={`Something went wrong, Please try  again later.`} />;

    return { success, error };
  };

  useEffect(() => {
    if (location.pathname === '/dismissed') {
      dispatch(getDismissedProfileList(1, messageAction()));
    } else {
      dispatch(getBlockedProfileList(1, messageAction()));
    }
  }, [location]);

  const getActivePagetTitle = () => {
    const activePage = location.pathname;

    const activeRoute = subRoutes.find(function(routeItem) {
      const regex = routeItem.active;
      const active = regex.test(activePage);

      return active ? routeItem : false;
    });

    return activeRoute;
  };

  const activePage = getActivePagetTitle();
  const isDismissedActive = activePage?.id === 'dismissed';
  const itemList = isDismissedActive ? dismissedProfiles : blockedItems;
  const itemPagination = isDismissedActive ? dismissedPagination : pagination;

  useEffect(() => {
    const hasMore = itemPagination?.current_page
      ? Boolean(itemPagination['current_page'] < itemPagination['total_pages'])
      : false;

    setMoreExists(hasMore);
  }, [itemPagination]);

  const unBlockProfile = (username: string, hashId: string) => {
    const successMsg = `${username} is now unblocked`;

    dispatch(toggleBlockProfile(username, hashId, false, true, navigate, messageAction(successMsg)));
  };

  const unHideProfile = (username: string, hashId: string) => {
    const successMsg = `${username} is now visible to you`;

    dispatch(deleteDismissProfile(hashId, messageAction(successMsg)));
  };

  const handleLoadMore = () => {
    isDismissedActive
      ? dispatch(
          getDismissedProfileList(
            !isEmpty(dismissedPagination) ? parseInt(dismissedPagination?.current_page, 10) + 1 : 1,
            messageAction()
          )
        )
      : dispatch(
          getBlockedProfileList(!isEmpty(pagination) ? parseInt(pagination?.current_page, 10) + 1 : 1, messageAction())
        );
  };

  return (
    <>
      <Container fullWidth screenTop>
        <LoadingBackdrop
          active={isDeletedLoader}
          customStyle={{
            position: 'fixed',
            alignItems: 'center',
            height: '100%',
            width: '100%',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10000,
          }}
        />
        <Content
          style={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            pointerEvents: isDeletedLoader ? 'none' : 'inherit',
          }}
        >
          <Aside style={{ width: '50%' }}>
            <TopNav
              className="blocked-sub-nav"
              subRoutes={subRoutes}
              customTopNavStyle={{ background: 'transparent' }}
              customStyleContainer={{ width: isBreakpointDown600 ? '100%' : 'inherit' }}
              customListStyle={{
                justifyContent: 'center',
                flexWrap: 'nowrap',
                gap: isBreakpointDown600 ? '30px' : '50px',
              }}
              themeDarkMode={isDarkMode()}
            />
          </Aside>
          <ListContainer className="list-container">
            <ListWrapper>
              <HeaderTitle>{activePage?.title}</HeaderTitle>
              <Caption>{activePage?.caption}</Caption>
              <Blocked
                pageId={activePage?.id}
                blockedItems={itemList}
                pagination={itemPagination}
                handleOnClick={isDismissedActive ? unHideProfile : unBlockProfile}
                isFetching={isFetchingProfileList}
                moreExists={moreExists}
                loadMore={handleLoadMore}
              />
            </ListWrapper>
          </ListContainer>
        </Content>
      </Container>
    </>
  );
};

export default BlockedDismissed;
